"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EcommerceCartSchema = exports.CheckoutStatusSchema = exports.SubstitutionPolicySchema = void 0;
const zod_1 = require("zod");
const tenant_payment_config_schema_1 = require("./tenant-payment-config.schema");
// NOTE: This is an ADDITIVE companion to the Phase A `cart.schema.ts`
// (`APosCartSchema`). The APos* schema is the persisted POS-side cart record;
// `EcommerceCartSchema` below is the storefront/ecommerce checkout cart used by
// ecommerce-service's cart CRUD + checkout (it carries the Phase E checkout/auth
// fields and an ISO-timestamped lifecycle). Kept side-by-side, not a rename.
exports.SubstitutionPolicySchema = zod_1.z.enum(['allow', 'refund_if_oos', 'contact_me']);
/** Where the cart is in the §6.1 step order (post-`start`, post-`authorize`). */
exports.CheckoutStatusSchema = zod_1.z.enum(['reserved', 'authorized', 'no_charge_pending', 'failed', 'hpp_pending']);
const AddressSchema = zod_1.z.object({
    firstName: zod_1.z.string().optional(),
    lastName: zod_1.z.string().optional(),
    street: zod_1.z.string().optional(),
    apt: zod_1.z.string().optional(),
    city: zod_1.z.string().optional(),
    state: zod_1.z.string().optional(),
    zip: zod_1.z.string().optional(),
}).passthrough();
const CartItemSchema = zod_1.z.object({
    variantId: zod_1.z.string(),
    productId: zod_1.z.string(),
    quantity: zod_1.z.number().int().positive(),
    /** Marketplace dimension (renamed from tenantIdForMarketplace per §6.1). Optional in tenant mode. */
    sellerTenantId: zod_1.z.string().optional(),
    marketplaceId: zod_1.z.string().optional(),
    locationIdHint: zod_1.z.string().optional(),
    addedAt: zod_1.z.string().datetime(),
    priceSnapshot: zod_1.z.number().nonnegative(),
    // --- Phase 5 pricing/bridge fields (additive, all optional) ---
    /** Tax IDs from VL.online.taxIds — used by recalculate + POS bridge. */
    taxIds: zod_1.z.array(zod_1.z.string()).optional(),
    /** Fee IDs from VL.online.feeIds — used by recalculate + POS bridge. */
    feeIds: zod_1.z.array(zod_1.z.string()).optional(),
    /** Active promotion ID from VL.online.promotionId. */
    promotionId: zod_1.z.string().optional(),
    /** Compliance preset ID from VL.online.compliancePresetId (drives age gate). */
    compliancePresetId: zod_1.z.string().optional(),
    /** Resolved compliance category from the preset row (e.g. 'tobacco', 'alcohol'). */
    complianceCategory: zod_1.z.string().optional(),
    /** Product/variant display name for POS bridge cart.items. */
    displayName: zod_1.z.string().optional(),
    /** Line image URL — persisted so a cart opened on another device/browser can
     *  render without the client-side meta cache. */
    imageUrl: zod_1.z.string().optional(),
    /** Variant label (e.g. "Large / Blue") — persisted for cross-device render. */
    variantLabel: zod_1.z.string().optional(),
    /** Supplier cost price for POS bridge (used in margin reporting). */
    costPrice: zod_1.z.number().optional(),
    /** Authoritative unit price written back by repriceCart (VL.online.price). */
    unitPrice: zod_1.z.number().optional(),
});
exports.EcommerceCartSchema = zod_1.z.object({
    cartId: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    storefrontMode: zod_1.z.enum(['tenant', 'marketplace']),
    marketplaceId: zod_1.z.string().optional(),
    customerId: zod_1.z.string().optional(),
    customerType: zod_1.z.enum(['retail', 'b2b']).optional(),
    items: zod_1.z.array(CartItemSchema),
    shippingAddress: AddressSchema.optional(),
    billingAddress: AddressSchema.optional(),
    customerNote: zod_1.z.string().optional(),
    // Totals (computed at checkout start)
    subtotal: zod_1.z.number().optional(),
    deliveryFee: zod_1.z.number().optional(),
    taxes: zod_1.z.array(zod_1.z.object({ name: zod_1.z.string(), amount: zod_1.z.number() })).optional(),
    discounts: zod_1.z.array(zod_1.z.object({ name: zod_1.z.string(), amount: zod_1.z.number() })).optional(),
    total: zod_1.z.number().optional(),
    status: zod_1.z.enum(['active', 'abandoned', 'converted', 'expired']),
    // --- Phase E checkout/auth fields (additive) ---
    checkoutStatus: exports.CheckoutStatusSchema.optional(),
    substitutionPolicy: exports.SubstitutionPolicySchema.optional(),
    /** Resolved by the fulfillment router — the merchant of record's location (§6.2). */
    fulfillingTenantId: zod_1.z.string().optional(),
    fulfillingLocationId: zod_1.z.string().optional(),
    chargeMode: tenant_payment_config_schema_1.ChargeModeSchema.optional(),
    /** True when chargeMode==='B' (pay-in-person at handoff, no online charge).
     *  Mirrors chargeMode as an explicit boolean so downstream consumers (order
     *  row, fulfillment) don't have to re-derive it from absence of an NMI auth. */
    payInPerson: zod_1.z.boolean().optional(),
    authBufferPct: zod_1.z.number().min(0).max(1).optional(),
    /** total * (1 + authBufferPct), rounded to cents. */
    authorizedAmount: zod_1.z.number().optional(),
    /** NMI transaction id of the auth (used by Phase F capture). */
    gatewayAuthTransactionId: zod_1.z.string().optional(),
    /** ISO — when the card hold expires (~7d). Phase F capture must beat this. */
    authExpiresAt: zod_1.z.string().datetime().optional(),
    reservationExpiresAt: zod_1.z.string().datetime().optional(),
    // --- Phase 2 Task 5: Dejavoo Hosted Payment Page (HPP) fields (additive) ---
    /** Our cart/order reference sent to iPOSpays as transactionReferenceId. */
    hppReferenceId: zod_1.z.string().optional(),
    /** The hosted page URL the shopper's browser was redirected to. */
    hppPaymentUrl: zod_1.z.string().optional(),
    /** iPOSpays transaction id once the HPP session completes (webhook/status). */
    hppTransactionId: zod_1.z.string().optional(),
    /** Last 4 digits of the card used on the HPP, once known. */
    hppLast4: zod_1.z.string().optional(),
    /** Tokenized card returned by queryPaymentStatus (preferences.requestCardToken). */
    hppCardToken: zod_1.z.string().optional(),
    /** iPOSpays consumer id tied to the tokenized card. */
    hppConsumerId: zod_1.z.string().optional(),
    // --- Checkout-loyalty fields (additive, all optional) ---
    loyaltyRedemption: zod_1.z.object({
        rewardId: zod_1.z.string(),
        pointsCost: zod_1.z.number(),
        discountAmount: zod_1.z.number(),
    }).optional(),
    pendingRegistration: zod_1.z.object({
        firstName: zod_1.z.string().optional(),
        lastName: zod_1.z.string().optional(),
        phone: zod_1.z.string(),
    }).optional(),
    loyaltyPointsEarned: zod_1.z.number().optional(),
    loyaltyRedeemed: zod_1.z.boolean().optional(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
    /** DynamoDB TTL epoch-seconds. */
    ttl: zod_1.z.number(),
});
//# sourceMappingURL=ecommerce-cart.schema.js.map